import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import AccountEditDialog from './AccountEditDialog';
import SortButton from './common/SortButton';
import SearchWindow from './common/SearchWindow';
import { MESSAGES } from '../common/messages';
import { sortTableData } from '../common/sortTableData';
import { isVerticalIpadMini, isHorizontalIpadMini } from '../common/responsiveUtils';
import { ACCOUNT_TYPES, SORT_CATEGORIES } from '../common/constants';

import Paper from '@mui/material/Paper';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Typography from '@mui/material/Typography';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';

import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/Delete';

import Papa from 'papaparse';
import { validateAccountType, validateClassName, validateEmployeeNumber, validateName, validatePassword }
  from '../common/validationUtils';

const Accounts = (props) => {
  const { typography, palette } = useTheme();

  const [accountsData, setAccountsData] = useState([]);
  const [accountTypeFilter, setAccountTypeFilter] = useState(ACCOUNT_TYPES.DRIVER.value);
  const [searchText, setSearchText] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState({
    _id: null,
    accountType: ACCOUNT_TYPES.DRIVER.value,
    employeeNumber: '',
    name: '',
    className: '',
    classNamePrefix: '',
    password: '',
  });
  const [isUpdate, setIsUpdate] = useState(true);

  const styles = {
    removeAllButton: {
      ...typography.body14bold,
      height: '44px',
      width: '124px',
      marginLeft: '24px',
      padding: '0px 0px',
      backgroundColor: 'white',
      color: palette.primary.main,
      border: '1px solid ' + palette.primary.main,
    },
    addAccountButton: {
      ...typography.body14bold,
      height: '44px',
      width: '112px',
      // marginLeft: 'auto',
      margin: '0 8px',
      backgroundColor: 'white',
      color: palette.primary.main,
      border: '1px solid ' + palette.primary.main,
    },
    csvDownloadButton: {
      ...typography.body14bold,
      height: '44px',
      width: '124px',
      backgroundColor: 'white',
      color: palette.primary.main,
      border: '1px solid ' + palette.primary.main,
    },
    driverChip: {
      ...typography.body14bold,
      'height': '44px',
      'padding': '0px 20px',
      'borderRadius': '100px',
      'marginLeft': '16px',
      'color': palette.white,
      'background': accountTypeFilter === ACCOUNT_TYPES.DRIVER.value ? palette.primary.light : palette.disabledCccccc,
      '&:hover': {
        background: accountTypeFilter === ACCOUNT_TYPES.DRIVER.value ? palette.primary.dark : palette.disabledAaaaaa,
      },
    },
    trainerChip: {
      ...typography.body14bold,
      'height': '44px',
      'padding': '0px 20px',
      'borderRadius': '100px',
      'marginLeft': '8px',
      'marginRight': 'auto',
      'color': palette.white,
      'background': accountTypeFilter === ACCOUNT_TYPES.TRAINER.value ? palette.primary.light : palette.disabledCccccc,
      '&:hover': {
        background: accountTypeFilter === ACCOUNT_TYPES.TRAINER.value ? palette.primary.dark : palette.disabledAaaaaa,
      },
    },
  };

  const fetchAccounts = async () => {
    try {
      const result = await axios.get('/v1/users');
      const copied = result.data.map((e) => {
        e.checked = false;
        return e;
      });
      setAccountsData(sortTableData(copied, SORT_CATEGORIES.EMPLOYEE_NUMBER_ASC));
    } catch (err) {
    }
  };
  useEffect(() => {
    fetchAccounts();
  }, [openEditDialog]);

  const deleteAccounts = async () => {
    const ids = accountsData.filter((e) => {
      return e.checked;
    }).map((e) => {
      return e._id;
    });
    try {
      await axios.delete('/v1/users', { data: { ids } });
      enqueueSnackbar(MESSAGES.ACCOUNT_DELETED);
    } catch (err) {
    }
    fetchAccounts();
  };

  const handleAddUsersByCSV = () => {
    const fileInput = document.getElementById('csvFileInput');
    fileInput.click();
    return;
  };

  function validateRow(row) {
    let errorMessage = '';
    errorMessage = validateEmployeeNumber(row.employeeNumber);
    if (errorMessage) {
      return 'employeeNumber is invalid: ' + errorMessage;
    }

    errorMessage = validateAccountType(row.accountType);
    if (errorMessage) {
      return 'accountType is invalid: ' + errorMessage;
    }

    errorMessage = validateName(row.name);
    if (errorMessage) {
      return 'name is invalid: ' + errorMessage;
    }

    errorMessage = validateClassName(row.className);
    if (errorMessage) {
      return 'className is invalid: ' + errorMessage;
    }

    errorMessage = validatePassword(row.password, false);
    if (errorMessage) {
      return 'password is invalid: ' + errorMessage;
    }
    return null;
  }

  const parseFile = async () => {
    const fileInput = document.getElementById('csvFileInput');
    const file = fileInput.files[0];

    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        encoding: 'UTF-8',
        complete: async (results) => {
          const jsonData = [];
          const errors = [];

          results.data.forEach((row) => {
            const error = validateRow(row);
            if (error) {
              errors.push(error);
            } else {
              jsonData.push(row);
            }

            // 回生情報が含まれない場合に空の文字列を暫定値とする
            if (row.className === null) row.className = '';

            // アカウントタイプをラベルから値に置き換える
            if (row.accountType === ACCOUNT_TYPES.DRIVER.label) row.accountType = ACCOUNT_TYPES.DRIVER.value;
            else if (row.accountType === ACCOUNT_TYPES.TRAINER.label) row.accountType = ACCOUNT_TYPES.TRAINER.value;
          });

          if (results.data.length === 0) {
            enqueueSnackbar(MESSAGES.NO_ACCOUNT_IN_CSV, { variant: 'error' });
            return;
          }

          if (errors.length > 0) {
            enqueueSnackbar(errors[0], { variant: 'error' });
            return;
          }

          try {
            await axios.post('/v1/users', jsonData);
            enqueueSnackbar(MESSAGES.ACCOUNT_UPDATED);
            fetchAccounts();
          } catch (error) {
          }
        },
      });
    }
    document.getElementById('csvFileInput').value = '';
  };

  return (
    <>
      <Container>
        <Stack direction='row' sx={{ margin: '16px 0px', height: '44px' }}>
          <p style={typography.body14bold}>アカウントの種類</p>
          <Chip
            label={ACCOUNT_TYPES.DRIVER.label}
            sx={styles.driverChip}
            variant={accountTypeFilter === ACCOUNT_TYPES.DRIVER.value ? 'selected' : 'filled'}
            onClick={() => {
              setAccountTypeFilter(ACCOUNT_TYPES.DRIVER.value);
            }}
          />
          <Chip
            label={ACCOUNT_TYPES.TRAINER.label}
            sx={styles.trainerChip}
            variant={accountTypeFilter === ACCOUNT_TYPES.TRAINER.value ? 'selected' : 'filled'}
            onClick={() => {
              setAccountTypeFilter(ACCOUNT_TYPES.TRAINER.value);
            }}
          />

          <Button
            sx={styles.addAccountButton}
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingAccount({
                accountType: ACCOUNT_TYPES.DRIVER.value,
                employeeNumber: '',
                name: '',
                className: '',
                classNamePrefix: '',
                password: '',
              });
              setIsUpdate(false);
              setOpenEditDialog(true);
            }}
            variant='outlined'
          >
            手動登録
          </Button>
          <input type="file" id="csvFileInput" accept=".csv" style={{ display: 'none' }} onChange={parseFile}></input>
          <Button
            sx={styles.csvDownloadButton}
            startIcon={<CloudUploadOutlinedIcon sx={{ width: '24px', margin: '0px' }} />}
            onClick={handleAddUsersByCSV}
            variant='outlined'
          >
            csv取込
          </Button>
        </Stack>
        <Stack direction='row' sx={{ padding: '8px 0px' }}>
          <SearchWindow
            searchText={searchText}
            setSearchText={setSearchText}
          />
          <SortButton
            items={[
              SORT_CATEGORIES.EMPLOYEE_NUMBER_ASC,
              SORT_CATEGORIES.EMPLOYEE_NAME_ASC,
              SORT_CATEGORIES.EC_ASC,
              SORT_CATEGORIES.EC_DESC,
            ]}
            initialItem={SORT_CATEGORIES.EMPLOYEE_NUMBER_ASC}
            tableData={accountsData}
            setTableData={setAccountsData}
          />
          <Button
            sx={styles.removeAllButton}
            startIcon={<DeleteIcon />}
            onClick={deleteAccounts}
            disabled={!accountsData.some((e) => e.checked)}
            variant='outlined'
          >
            一括削除
          </Button>
        </Stack>
        <AccountTable
          accountsData={accountsData}
          setAccountsData={(accountsData) => {
            setAccountsData(accountsData);
          }}
          filter={(e) => {
            // 名前・社員番号による絞り込み
            const nameFilter = searchText === '' || (e.employeeNumber + e.name).includes(searchText);
            // 運転士・指導担当のアカウント種別による絞り込み
            const driverFilter =
              (e.accountType === ACCOUNT_TYPES.DRIVER.value && accountTypeFilter === ACCOUNT_TYPES.DRIVER.value);
            const trainerFilter =
              (e.accountType === ACCOUNT_TYPES.TRAINER.value && accountTypeFilter === ACCOUNT_TYPES.TRAINER.value);
            return nameFilter && (driverFilter || trainerFilter);
          }}
          handleClicked={(id) => {
            const account = structuredClone(accountsData.find((e) => e._id === id));
            account.password = '';
            account.classNamePrefix = account.className.replace(/[0-9]/g, '');
            account.className = account.className.replace(/\D/g, '');
            setEditingAccount(account);
            setIsUpdate(true);
            setOpenEditDialog(true);
          }}
        />
      </Container>
      <AccountEditDialog
        open={openEditDialog}
        setOpen={setOpenEditDialog}
        account={editingAccount}
        setAccount={(account) => {
          setEditingAccount(account);
        }}
        isUpdate={isUpdate}
        loginUser={props.loginUser}
      />
    </>
  );
};

// アカウント一覧表示用テーブル
const AccountTable = (props) => {
  const { typography, palette } = useTheme();
  // テーブルの高さをPC, iPadMini(縦), iPadMini(横)で分ける
  let maxTableHeight = '430px';
  if (isVerticalIpadMini()) maxTableHeight = '630px';
  if (isHorizontalIpadMini()) maxTableHeight = '400px';
  const styles = {
    tableCellHead: {
      ...typography.body14bold,
      color: palette.darkgray,
    },
    tableCellBody: {
      ...typography.body14,
    },
    noAccountMessage: {
      ...typography.body16bold,
      color: palette.darkgray,
      align: 'center',
    },
  };

  // APIのレスポンスデータを表に表示する形式に加工
  const rows = props.accountsData.filter((e) => {
    return props.filter(e);
  }).map((e) => {
    return {
      _id: e._id,
      checked: e.checked,
      employeeNumber: e.employeeNumber,
      name: e.name,
      accountTypeLabel: Object.values(ACCOUNT_TYPES).find((type) => type.value === e.accountType)?.label,
      accountType: e.accountType,
      className: e.className ? e.className + '回生' : '',
      createdDate: `${dayjs(e.createdAt).format('YYYY/MM/DD')}`,
    };
  });

  return (
    <TableContainer component={Paper} sx={{ maxHeight: maxTableHeight, overflowY: 'unset' }}>
      <Table>
        <TableHead sx={{ background: palette.backgroundBlue, position: 'sticky', top: 0, zIndex: 1 }}>
          <TableRow>
            <TableCell sx={styles.tableCellHead}>社員番号</TableCell>
            <TableCell sx={styles.tableCellHead}>
              氏名</TableCell>
            <TableCell sx={styles.tableCellHead} >
              EC</TableCell>
            <TableCell sx={styles.tableCellHead}>
              アカウント種類</TableCell>
            <TableCell sx={styles.tableCellHead} >
              作成年月日</TableCell>
            <TableCell align='center' sx={styles.tableCellHead} >
              <Checkbox sx={{ color: palette.primary.main, padding: '0px' }} onChange={(event) => {
                const employeeNumbers = rows.map((e) => {
                  return e.employeeNumber;
                });
                const arr = props.accountsData.map((e) => {
                  if (employeeNumbers.filter((employeeNumber) => {
                    return employeeNumber === e.employeeNumber;
                  }).length > 0) e.checked = event.target.checked;
                  return e;
                });
                props.setAccountsData(arr);
              }} />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* アカウントがない場合の表示 */}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align='center' height='390px'>
                <Typography sx={styles.noAccountMessage}>表示条件を満たすアカウントが登録されていません</Typography>
                <Typography sx={styles.noAccountMessage}>右上の「手動登録」ボタンから</Typography>
                <Typography sx={styles.noAccountMessage}>新しいアカウントを作成してください</Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow
              hover
              key={row.employeeNumber}
              onClick={() => {
                props.handleClicked(row._id);
              }}
            >
              <TableCell sx={styles.tableCellBody} component="th" >
                {row.employeeNumber}
              </TableCell>
              <TableCell sx={styles.tableCellBody} >
                {row.name}</TableCell>
              <TableCell sx={styles.tableCellBody} >
                {row.className}</TableCell>
              <TableCell sx={styles.tableCellBody} >
                {row.accountTypeLabel}</TableCell>
              <TableCell sx={styles.tableCellBody} >
                {row.createdDate}</TableCell>
              <TableCell align='center' sx={styles.tableCellBody} >
                <Checkbox
                  sx={{ color: palette.primary.main, padding: '0px' }}
                  checked={row.checked}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => {
                    const arr = props.accountsData.concat();
                    arr.map((e, i) => {
                      if (e.employeeNumber === row.employeeNumber) {
                        arr[i].checked = event.target.checked;
                      }
                    });
                    props.setAccountsData(arr);
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default Accounts;

Accounts.propTypes = {
  loginUser: PropTypes.object,
};
AccountTable.propTypes = {
  setAccountsData: PropTypes.func,
  accountsData: PropTypes.array,
  filter: PropTypes.func,
  handleClicked: PropTypes.func,
};
